import { faEdit, faFile } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Toast } from '@modusign/design-system';
import {
  getExternalDocumentRoleList,
  useChangeExternalDocumentRoles,
} from '@modusign/openapi-client-external-document';
import { useState } from 'react';
import { useQuery } from 'react-query';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import {
  ResourceModal,
  ResourceModalFooter,
  ResourceModalHeaderWithBackButton,
} from '@app/components/Modal/ResourceModal';
import {
  AddResourceRoleModalContent,
  ResourceRoleListContent,
  useResourceRolesDiff,
} from '@app/features/authorization';
import { useExternalDocumentPermission } from '@app/features/externalDocument/hooks/useExternalDocumentPermission';
import { ExternalDocumentResourceRoleName } from '@app/features/externalDocument/types';
import { adaptWorkspaceGroup, adaptWorkspaceMember } from '@app/features/workspaceManagement/types';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import pushAdobeDataLayer from '@app/libs/analytics/adobe';
import getAllList from '@app/libs/getAllList';

interface SingleDocumentResourceRoleModalProps {
  document: { id: string; title: string };
  onClose: () => void;
  onComplete: () => void;
}

export default function SingleDocumentResourceRoleModal({
  document,
  onClose,
  onComplete,
}: SingleDocumentResourceRoleModalProps) {
  const { createErrorHandler, handleError } = useOpenErrorModal();
  const workspaceId = useWorkspaceId();
  const [isAddingResourceRole, setAddingResourceRole] = useState(false);
  const {
    currentResourceRoles,
    updateResourceRoles,
    changes,
    removeResourceRoles,
    setInitialResourceRoles,
  } = useResourceRolesDiff<ExternalDocumentResourceRoleName>();

  const { isLoading } = useQuery({
    queryKey: ['externalDocumentResourceRoleList', document.id],
    queryFn: async () =>
      getAllList({
        queryFn: (offset, limit) =>
          getExternalDocumentRoleList(workspaceId, document.id, {
            limit,
            offset,
          }).then(({ count, roles }) => ({ count, list: roles })),
      }),
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      setInitialResourceRoles(
        data.map((d) => ({
          name: d.name,
          subject:
            d.subjectType === 'GROUP'
              ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                adaptWorkspaceGroup(d.group!)
              : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                adaptWorkspaceMember(d.member!),
        }))
      );
    },
  });

  const { isLoading: isUpdating, mutate } = useChangeExternalDocumentRoles({
    mutation: {
      onError: createErrorHandler({ title: '공유 수정 실패' }),
      onSuccess: (result) => {
        if (result.error === 0) {
          pushAdobeDataLayer({
            event: 'changeExternalDocumentRole',
          });
          Toast.success({
            description: `외부문서의 공유 설정을 적용했습니다.`,
          });
        } else {
          handleError({
            title: '공유 수정 실패',
            error: new Error(result.results[0].error?.title || ''),
          });
        }
      },
      onSettled: onComplete,
    },
  });

  const { assignPermission } = useExternalDocumentPermission(document.id);

  return (
    <ResourceModal isOpen>
      <ResourceModalHeaderWithBackButton
        resourceName={document.title}
        title="공유"
        onBack={isAddingResourceRole ? () => setAddingResourceRole(false) : undefined}
        onClose={onClose}
      />
      {isLoading && <CenterLoadingSpinner />}
      {!isLoading && isAddingResourceRole ? (
        <AddResourceRoleModalContent
          preGrantedResourceRoles={currentResourceRoles.map((rr) => rr.subject)}
          roleOptions={[
            {
              roleName: 'VIEWER',
              name: '조회 가능',
              content: '조회만 가능',
              icon: <FontAwesomeIcon icon={faFile} />,
            },
            {
              roleName: 'EDITOR',
              name: '편집자',
              content: '조회·수정·삭제 가능',
              icon: <FontAwesomeIcon icon={faEdit} />,
            },
          ]}
          onCancel={() => setAddingResourceRole(false)}
          onSubmit={(resourceRoles) => {
            mutate({
              workspaceId,
              data: {
                roles: resourceRoles.map((rr) => ({
                  externalDocumentId: document.id,
                  subjectType: rr.subject.subjectType,
                  subjectId: rr.subject.subjectId,
                  name: rr.name,
                })),
              },
            });
          }}
        />
      ) : (
        <ResourceRoleListContent
          hasEditPermission={assignPermission.isPermitted}
          resourceRoleTypes={[
            { name: 'VIEWER', description: '열람 가능' },
            { name: 'EDITOR', description: '편집자' },
          ]}
          resourceRoles={currentResourceRoles}
          onAddResourceRoleClick={() => setAddingResourceRole(true)}
          onChangeResourceRole={updateResourceRoles}
          onRemoveResourceRole={removeResourceRoles}
        />
      )}
      {!isAddingResourceRole && assignPermission.isPermitted && (
        <ResourceModalFooter justifyContent="flex-end">
          <Button
            disabled={changes.length === 0}
            isLoading={isUpdating}
            variant="primary"
            onClick={() => {
              mutate({
                workspaceId,
                data: {
                  roles: changes.map(({ name, subject }) => {
                    if (!name) {
                      throw new Error('role name is null.');
                    }
                    return {
                      externalDocumentId: document.id,
                      subjectType: subject.subjectType,
                      subjectId: subject.subjectId,
                      name,
                    };
                  }),
                },
              });
            }}
          >
            {changes.length === 0 ? '적용' : '변경사항을 적용'}
          </Button>
        </ResourceModalFooter>
      )}
    </ResourceModal>
  );
}
